import { Box } from '@mui/material'
import { IInsuranceData } from '../../../../services/entityServices'
import { ReactElement, useMemo } from 'react'
import KeyValueGrid, {
    IKeyValue,
    IValueType,
} from '../../../Common/KeyValueGrid'
import {
    isCurrency,
    isDateTime,
    isEntity,
    isPDFBase64,
} from '../../../../utils/AttributeUtils'
import CertificateViewer from '../../../Common/Base64ToPDF'
import { isPlainObject } from '../../../../utils/MiscellaneousUtils'
import DatetimeTypeData from '../../../Common/DatetimeTypeData'
import { AttributeType } from '../../../../constants/constants'

interface InsuranceTopViewItemsPropsType {
    insuranceData: IInsuranceData
}

const insuranceReadAttributesIds = [
    'insuranceStatus',
    'pdfCertificateField',
    'separator',
    'quotePremiumAmount',
    'quoteCertificateNumber',
    'quoteExpiryDate',
]
const InsuranceTopViewItems = (
    props: InsuranceTopViewItemsPropsType,
): ReactElement => {
    const { insuranceData } = props
    const viewData: IKeyValue[] = useMemo(() => {
        if (!insuranceData?.attributes) return []

        const attributesMap = new Map(
            insuranceData.attributes.map((attr) => [attr.id, attr]),
        )

        const result: IKeyValue[] = []

        insuranceReadAttributesIds.forEach((id) => {
            // Handle separator
            if (id === 'separator') {
                result.push({
                    key: '',
                    value: '',
                    type: AttributeType.SEPARATOR,
                })
                return
            }

            const i = attributesMap.get(id)
            if (!i) return

            // Special case: quoteExpiryDate only if value exists
            if (id === 'quoteExpiryDate' && !i.value) return

            if (isEntity(i)) {
                result.push({
                    key: i.name,
                    value: i.value?.uid,
                    type: 'STRING',
                })
            } else if (isPDFBase64(i)) {
                result.push({
                    key: i.name,
                    value: <CertificateViewer pdfBase64={i.value} />,
                    type: 'COMPONENT',
                })
            } else if (isDateTime(i)) {
                result.push({
                    key: i.name,
                    value: isPlainObject(i.value) ? (
                        <DatetimeTypeData
                            dataValue={i.value}
                            showTimezone={false}
                        />
                    ) : (
                        i.value
                    ),
                    type: isPlainObject(i.value) ? 'COMPONENT' : 'STRING',
                })
            } else if (isCurrency(i)) {
                result.push({
                    key: i.name,
                    value: i.value ? `${i.value} ${i.unit}` : '',
                    type: 'STRING',
                })
            } else {
                result.push({
                    key: i.name,
                    value: i.value,
                    type: i.dataType.toUpperCase() as IValueType,
                })
            }
        })

        return result
    }, [insuranceData])

    return (
        <Box sx={{ width: 'calc(100%)' }}>
            <KeyValueGrid
                data={viewData}
                minWidth="150px"
                maxWidth="calc(33% - 20px)"
            />
        </Box>
    )
}
export default InsuranceTopViewItems
